import { useFrame, useThree } from '@react-three/fiber'
import { RefObject, useEffect, useRef } from 'react'
import {
    Color,
    FrontSide,
    Group,
    Mesh,
    MeshBasicMaterial,
    NormalBlending,
    PlaneGeometry,
} from 'three'
import { store } from '@/src/stores/store'
import {
    type FootstepSide,
    type FootstepStepEvent,
    type FootstepTimelineRef,
    computeFootPlacement,
    footprintSettings,
} from './footstepConfig'
import { useFootstepEffectTextures } from './useFootstepEffectTextures'

const GLOW_FOOTPRINT_COLORS = [
    '#ff1744', '#ff9100', '#ffea00', '#76ff03',
    '#00e5ff', '#2979ff', '#7c4dff', '#f500ff',
] as const

function getRandomGlowFootprintColor(): string {
    return GLOW_FOOTPRINT_COLORS[Math.floor(Math.random() * GLOW_FOOTPRINT_COLORS.length)]
}

type FootprintEntry = {
    age: number
    geometry: PlaneGeometry
    material: MeshBasicMaterial
    mesh: Mesh<PlaneGeometry, MeshBasicMaterial>
    side: FootstepSide
}

type EffectRunFootstepsProps = {
    anchorRef: RefObject<Group | null>
    timelineRef: FootstepTimelineRef
}

export function EffectRunFootsteps({ anchorRef, timelineRef }: EffectRunFootstepsProps) {
    const { scene } = useThree()

    const activeFootprintsRef = useRef<FootprintEntry[]>([])
    const footstepGeometryRef = useRef(new PlaneGeometry(1, 1))

    const { footprintTexture } = useFootstepEffectTextures()

    useEffect(() => {
        return () => {
            for (const f of activeFootprintsRef.current) {
                scene.remove(f.mesh)
                f.material.dispose()
            }
            footstepGeometryRef.current.dispose()
        }
    }, [])

    function spawnFootprint(step: FootstepStepEvent) {
        const placement = computeFootPlacement(anchorRef, step.side)
        if (!placement) return

        const footprintColor = store.footstepEffect === 'glow'
            ? getRandomGlowFootprintColor()
            : footprintSettings.color

        const footprintOpacity = store.footstepEffect === 'glow'
            ? footprintSettings.opacityGlow
            : footprintSettings.opacity

        const material = new MeshBasicMaterial({
            color: new Color(footprintColor),
            alphaMap: footprintTexture,
            transparent: true,
            alphaTest: 0.25,
            opacity: footprintOpacity,
            depthWrite: false,
            blending: NormalBlending,
            side: FrontSide,
            toneMapped: false,
        })

        const mesh = new Mesh(footstepGeometryRef.current, material)
        mesh.position.copy(placement.position)
        mesh.quaternion.copy(placement.surfaceQuat)
        mesh.scale.set(
            step.side === 'left' ? footprintSettings.size : -footprintSettings.size,
            footprintSettings.size,
            1,
        )
        scene.add(mesh)

        activeFootprintsRef.current.push({
            age: 0,
            geometry: footstepGeometryRef.current,
            material,
            mesh,
            side: step.side,
        })
    }

    useFrame((_, delta) => {
        const safeDelta = Math.min(delta, 1 / 60)
        const timeline = timelineRef.current
        for (let i = activeFootprintsRef.current.length - 1; i >= 0; i--) {
            const f = activeFootprintsRef.current[i]
            f.age += safeDelta
            const t = f.age / footprintSettings.lifetime
            if (t >= 1) {
                scene.remove(f.mesh)
                f.material.dispose()
                activeFootprintsRef.current.splice(i, 1)
                continue
            }
            f.material.opacity = footprintSettings.opacity * (1 - t)
        }
        if (!timeline.footstepsEnabled) return
        for (const step of timeline.steps) {
            spawnFootprint(step)
        }
    })

    return null
}

